import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, cents, day, useMe } from '../../lib'
import { Empty, ErrorBox, Loading } from '../../app/ui'
import { RESULT_LABEL, SpendPage, months, putFile, statusText } from './common'
import type { Batch, Counts } from './common'

type Row = {
  row_no: number; raw: Record<string, string>; errors: string[]; result: string
  mapped: { kind: string; amount_cents: number; occurred_at: string; customer: { external_id: string } } | null
}

// Rows that will not count, and why, in the order a partner cares about.
const SKIPPED = ['invalid', 'conflict', 'pre_join', 'unmatched', 'overlap_review', 'skipped'] as const
const NOTE: Record<string, [string, string]> = {
  invalid: ['1 row has a problem', 'rows have a problem'],
  conflict: ['1 row clashes with what you sent before', 'rows clash with what you sent before'],
  pre_join: ['1 row is from before the customer joined us. It earns nothing', 'rows are from before the customer joined us. They earn nothing'],
  unmatched: ['1 row is for a customer who has not signed up yet. It counts once they do', 'rows are for customers who have not signed up yet. They count once they do'],
  overlap_review: ['1 row was also sent another way. We check it', 'rows were also sent another way. We check them'],
  skipped: ['1 row earns nothing: it was not paid, not in USD or names no customer. See rows for why', 'rows earn nothing: they were not paid, not in USD or name no customer. See rows for why'],
}
const KIND: Record<string, string> = {
  cash_paid: 'Payment', invoice_sent: 'Invoice', paid_out_of_band: 'Invoice', refund: 'Refund',
  dispute_lost: 'Dispute', void: 'Void', correction: 'Correction',
}

export function BatchPage() {
  const { id } = useParams()
  return <SpendPage>{(_pid, canEdit) => <Body id={id!} canEdit={canEdit} />}</SpendPage>
}

function Body({ id, canEdit }: { id: string; canEdit: boolean }) {
  const qc = useQueryClient()
  const done = () => qc.invalidateQueries({ queryKey: ['ingest'] })
  const b = useQuery({
    queryKey: ['ingest', 'batch', id],
    queryFn: () => api<Batch>('GET', `/api/batches/${id}`),
    refetchInterval: (q) => (q.state.data && ['received', 'approved'].includes(q.state.data.status) && !q.state.data.error ? 1000 : false),
  })
  if (!b.data) return b.error ? <ErrorBox error={b.error} /> : <Loading />
  const batch = b.data
  const p = batch.preview
  return (
    <div className="stack">
      <h1>{batch.filename ?? 'Your file'}</h1>
      <p>
        <span data-testid="ingest-batch-status">{statusText(batch)}</span>
        {' · '}{batch.channel === 'email' ? `emailed by ${batch.sender ?? 'someone'}` : 'sent'} {day(batch.received_at)}
      </p>
      {batch.error && <div className="error-box" role="alert">{batch.error}</div>}

      {batch.status === 'previewed' && batch.needs_approval_reason === 'missing_column' && p?.missing && (
        canEdit ? <AskColumn id={id} p={p} onDone={done} /> : <p>{p.question}</p>
      )}
      {p && p.missing === undefined && <Summary batch={batch} p={p} />}
      {batch.status === 'previewed' && p && !p.missing && (
        canEdit ? <Confirm batch={batch} p={p} onDone={done} /> : <p className="muted">An owner or admin on your team confirms this file.</p>
      )}
      {canEdit && batch.channel === 'upload' && (batch.status === 'failed' || (batch.status === 'received' && batch.sha256 === null && stalled(batch))) && (
        <Again id={id} onDone={done} />
      )}
      {p && !p.missing && p.rows > 0 && <Rows id={id} status={batch.status} />}
    </div>
  )
}

function Summary({ batch, p }: { batch: Batch; p: Counts }) {
  const skipped = SKIPPED.filter((k) => p[k] > 0)
  if (p.rows === 0) return <p data-testid="ingest-summary">This file has no rows.</p>
  return (
    <section className="panel stack">
      <p data-testid="ingest-summary" style={{ fontSize: '1.4rem' }}>
        {p.customers === 1 ? '1 customer' : `${p.customers ?? 0} customers`}, {cents(p.total_cents ?? 0)},{' '}
        {months(batch.period_start ?? p.first_month, batch.period_end ?? p.last_month)}
      </p>
      {p.duplicate_of && p.same === p.rows && <p className="muted">You sent this same file before. Nothing in it is new.</p>}
      {skipped.map((k) => (
        <p key={k} className="muted" data-testid={`ingest-preview-${k}`}>
          {p[k] === 1 ? NOTE[k][0] : `${p[k]} ${NOTE[k][1]}`}.
        </p>
      ))}
      <span hidden data-testid="ingest-preview-new">{p.new}</span>
    </section>
  )
}

function Confirm({ batch, p, onDone }: { batch: Batch; p: Counts; onDone: () => void }) {
  const from = (batch.period_start ?? p.first_month ?? '').slice(0, 7)
  const to = (batch.period_end ?? p.last_month ?? '').slice(0, 7)
  const [start, setStart] = useState(from)
  const [end, setEnd] = useState(to)
  const [all, setAll] = useState(true)
  useEffect(() => { setStart(from); setEnd(to) }, [from, to])
  const nothing = p.rows === p.invalid + p.conflict + (p.skipped ?? 0)
  const approve = useMutation({
    mutationFn: () => api('POST', `/api/batches/${batch.id}/approve`, {
      preview_hash: batch.preview_hash, all_spend: all,
      period_start: start ? `${start}-01` : null, period_end: end ? `${end}-01` : null,
    }),
    onSuccess: onDone,
  })
  const reject = useMutation({ mutationFn: () => api('POST', `/api/batches/${batch.id}/reject`), onSuccess: onDone })
  const range = months(start ? `${start}-01` : null, end ? `${end}-01` : null)
  return (
    <section className="stack">
      {range && (
        <label>
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="ingest-complete" />{' '}
          This is all your customers' spend for {range}
        </label>
      )}
      <details>
        <summary>Change months</summary>
        <div className="row">
          <label>From <input type="month" value={start} onChange={(e) => setStart(e.target.value)} data-testid="ingest-period-start" /></label>
          <label>To <input type="month" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="ingest-period-end" /></label>
        </div>
      </details>
      <div className="row">
        <button type="button" className="primary" data-testid="ingest-approve" disabled={approve.isPending || nothing}
          title={nothing ? 'No row in this file can count' : undefined} onClick={() => approve.mutate()}>
          Looks right
        </button>
        <button type="button" className="link-button" data-testid="ingest-reject" disabled={reject.isPending} onClick={() => reject.mutate()}>
          Not right
        </button>
      </div>
      {nothing && <p className="muted">No row in this file can count. Fix the file and <Link to="/partner/spend/upload">send it again</Link>.</p>}
      {approve.error && <ErrorBox error={approve.error} />}
      {reject.error && <ErrorBox error={reject.error} />}
    </section>
  )
}

/** The one question when a column we need is missing: pick it from the file's own columns. */
function AskColumn({ id, p, onDone }: { id: string; p: Counts; onDone: () => void }) {
  const [header, setHeader] = useState('')
  const pick = useMutation({
    mutationFn: () => api('POST', `/api/batches/${id}/columns`, { need: p.missing, header }),
    onSuccess: onDone,
  })
  return (
    <section className="panel stack" data-testid="ingest-ask">
      <p><strong>{p.question}</strong></p>
      <div className="row">
        <select value={header} onChange={(e) => setHeader(e.target.value)} data-testid="ingest-ask-column" aria-label="Column">
          <option value="">Pick a column</option>
          {(p.headers ?? []).map((h) => (
            <option key={h} value={h}>{h}{p.samples?.[h]?.[0] ? ` (e.g. ${p.samples[h][0]})` : ''}</option>
          ))}
        </select>
        <button type="button" className="primary" disabled={!header || pick.isPending} onClick={() => pick.mutate()} data-testid="ingest-ask-submit">
          Use this column
        </button>
      </div>
      {pick.error && <ErrorBox error={pick.error} />}
    </section>
  )
}

function Again({ id, onDone }: { id: string; onDone: () => void }) {
  const support = useMe().me?.support_email
  const again = useMutation({
    mutationFn: (file: File) => {
      if (file.size === 0) throw new Error('This file is empty.')
      return putFile(`/api/uploads/${id}`, file)
    },
    onSettled: onDone,
  })
  return (
    <section className="panel stack" data-testid="ingest-retry">
      <label className="button">
        {again.isPending ? 'Uploading…' : 'Upload it again'}
        <input type="file" style={{ display: 'none' }} data-testid="ingest-retry-file" disabled={again.isPending}
          onChange={(e) => e.target.files?.[0] && again.mutate(e.target.files[0])} />
      </label>
      {support && <p className="muted">If it fails twice, write to <a href={`mailto:${support}`}>{support}</a>.</p>}
      {again.error && <ErrorBox error={again.error} />}
    </section>
  )
}

function Rows({ id, status }: { id: string; status: Batch['status'] }) {
  const [open, setOpen] = useState(false)
  const rows = useQuery({
    queryKey: ['ingest', 'rows', id, status],
    queryFn: () => api<{ rows: Row[]; next_cursor: string | null }>('GET', `/api/batches/${id}/rows`),
    enabled: open && !['received', 'failed'].includes(status),
  })
  return (
    <details onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>See rows</summary>
      {!rows.data ? <Loading /> : rows.data.rows.length === 0 ? <Empty>No rows.</Empty> : (
        <div className="table-wrap">
          <table data-testid="ingest-rows">
            <thead><tr><th>#</th><th>Customer</th><th>What</th><th>Amount</th><th>Date</th><th /></tr></thead>
            <tbody>
              {rows.data.rows.map((r) => (
                <tr key={r.row_no}>
                  <td className="num">{r.row_no}</td>
                  <td>{r.mapped?.customer.external_id ?? r.raw.partner_customer_id ?? ''}</td>
                  <td>{r.mapped ? KIND[r.mapped.kind] ?? r.mapped.kind : ''}</td>
                  <td className="num">{r.mapped ? cents(r.mapped.amount_cents) : r.result === 'skipped' && r.raw.amount_usd ? `$${r.raw.amount_usd}` : ''}</td>
                  <td>{r.mapped ? day(r.mapped.occurred_at) : r.result === 'skipped' && r.raw.occurred_at ? day(r.raw.occurred_at.slice(0, 10)) : ''}</td>
                  <td>{r.result === 'new' ? '' : RESULT_LABEL[r.result] ?? r.result}{r.errors.length > 0 && <div className={r.result === 'skipped' ? 'muted' : 'negative'}>{r.errors.join(' ')}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  )
}

/** A file that never arrived: the upload was cut off more than a minute ago. */
function stalled(b: Batch): boolean {
  return Date.now() - new Date(b.received_at).getTime() > 60_000
}
